import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { PrepaymentTopUpBlockConfig } from '@front-platform/content-builder';
import { prepaymentTopUpBlockResolveConfig } from '@front-platform/content-builder';
import { Component, computed, effect, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TopUpEvent } from '@core/services/prepayment/top-up.directive';
import {
  FreeAmountButtonPosition,
  PrepaymentTopUpComponent,
} from '@shared/components/prepayment-top-up/prepayment-top-up.component';
import { Router } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, switchMap, withLatestFrom } from 'rxjs';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { ShopsService } from '@core/services/shops/shops.service';
import { ShopStoreDispatcher } from '@common/dispatchers/shop.dispatcher';
import { navigateToShopPage } from '@core/services/navigate/navigate-to-shop-page';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { ShopPickerEntities } from '@core/models/shop.model';
export interface PrepaymentTopUpBlockRuntimeConfig {
  'shop_entity'?: ShopPickerEntities;
}

export type PrepaymentTopUpBlockConfigInput = Partial<
  Omit<PrepaymentTopUpBlockConfig, keyof PrepaymentTopUpBlockRuntimeConfig> &
    PrepaymentTopUpBlockRuntimeConfig
>;
export type PrepaymentTopUpBlockResolvedConfig = Omit<
  PrepaymentTopUpBlockConfig,
  keyof PrepaymentTopUpBlockRuntimeConfig
> &
  Partial<PrepaymentTopUpBlockRuntimeConfig>;
export interface PrepaymentTopUpBlockContract extends ConfigComponentContract<
  PrepaymentTopUpBlockConfigInput,
  PrepaymentTopUpBlockResolvedConfig
> {}

@Component({
  selector: 'app-prepayment-top-up-block',
  standalone: true,
  imports: [CommonModule, PrepaymentTopUpComponent],
  template: `<app-prepayment-top-up
    [freeAmountButtonPosition]="freeAmountButtonPosition()"
    (topUpEvents)="onTopUpEvent($event)"
  />`,
})
export class PrepaymentTopUpBlockComponent implements PrepaymentTopUpBlockContract {
  readonly config = input<PrepaymentTopUpBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    PrepaymentTopUpBlockConfig,
    PrepaymentTopUpBlockRuntimeConfig
  >(prepaymentTopUpBlockResolveConfig, () => ({ config: this.config() }));
  // ! This block can be only loaded under shop routes (the shopSlug param must be present)
  private readonly shopStoreSelector = inject(ShopStoreSelector);
  private readonly shopsService = inject(ShopsService);
  private readonly shopStoreDispatcher = inject(ShopStoreDispatcher);
  private readonly router = inject(Router);
  private readonly navigateToShopPage = inject(navigateToShopPage);
  private readonly routerHelperService = inject(RouterHelperService);

  public readonly shopEntity = computed(
    () => this.resolvedConfig()['shop_entity'] as unknown as ShopPickerEntities,
  );
  public readonly freeAmountButtonPosition = computed(
    () =>
      this.resolvedConfig()['free_amount_button_position'] as unknown as FreeAmountButtonPosition,
  );

  private readonly shopId = computed(() => {
    const shop = this.shopEntity()?.[0];
    return shop ? shop.id : '';
  });

  private readonly shopId$ = toObservable(this.shopId);

  constructor() {
    effect(() => {
      if (!this.shopId()) {
        return;
      }

      if (this.shopStoreSelector.selectIsLoaded() || this.shopStoreSelector.selectIsLoading()) {
        return;
      }
      return this.shopsService.loadShopById$(this.shopId());
    });
    this.shopId$
      .pipe(
        withLatestFrom(
          this.shopStoreSelector.selectIsLoaded$,
          this.shopStoreSelector.selectIsLoading$,
        ),
        switchMap(([shopId, isLoaded, isLoading]) => {
          if (!shopId) {
            return EMPTY;
          }
          if (isLoaded || isLoading) {
            return EMPTY;
          }
          this.shopStoreDispatcher.updateIsLoading(true);

          return this.shopsService.loadShopById$(shopId);
        }),
        catchError(() => EMPTY),
        takeUntilDestroyed(),
      )
      .subscribe(shop => {
        if (shop) {
          this.shopStoreDispatcher.insertActive(shop);
        }
        this.shopStoreDispatcher.updateIsLoading(false);
      });
  }
  protected onTopUpEvent(event: TopUpEvent) {
    console.log('event', event);
    if (event.type === 'Cancelled') {
      const activeShop = this.shopStoreSelector.selectActive();
      if (activeShop) {
        this.navigateToShopPage(activeShop);
      }
      return;
    }
    if (event.type === 'Top up added') {
      this.router.navigateByUrl(this.routerHelperService.translateRoute('cart'));
      return;
    }
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(PrepaymentTopUpBlockComponent, { 'keyAliases': {} });
